import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ACCEPTABLE_ERROR_PX, MAX_CALIBRATION_ATTEMPTS } from '../../utils/webgazerController';

/**
 * GazeCalibration
 *
 * Full-screen step shown before answering when a questionnaire has eye-tracking
 * enabled. Drives WebGazer through the hook: prepare (camera) → calibrate
 * (9-point, 2-lap) → accuracy test. The respondent must reach the acceptable
 * error (<200px) or retry; after MAX_CALIBRATION_ATTEMPTS they may continue
 * without eye tracking. The outcome is persisted onto the session and, on
 * success, live gaze capture is started before the first question.
 *
 * Props:
 *   recording  – the useQuestionnaireRecording() instance
 *   onComplete – called (no args) once setup finishes and Q1 should begin
 */
const GazeCalibration = ({ recording, onComplete }) => {
  const { t } = useTranslation();
  // intro | preparing | calibrating | testing | fail | exhausted | passed | error
  const [phase, setPhase] = useState('intro');
  const [status, setStatus] = useState('');
  const [lastError, setLastError] = useState(null);
  const [busy, setBusy] = useState(false);

  const containerRef = useRef(null);
  const controllerRef = useRef(null);
  const bestErrorRef = useRef(null);
  const attemptRef = useRef(0);

  const finish = useCallback(
    async (finalStatus) => {
      const errorPx = bestErrorRef.current != null ? Math.round(bestErrorRef.current) : null;
      await recording.persistCalibration(finalStatus, errorPx, attemptRef.current);
      if (finalStatus === 'passed') {
        recording.beginGazeCapture();
      } else {
        recording.stopGaze();
      }
      onComplete();
    },
    [recording, onComplete]
  );

  // Step 1: prepare WebGazer (first round only) and run the 9-point calibration.
  // On success, gate the accuracy test behind its own instruction screen instead
  // of running it back-to-back — the respondent starts the test themselves.
  const runCalibration = useCallback(async () => {
    setBusy(true);
    try {
      // Prepare WebGazer (camera) on the first round.
      if (!controllerRef.current) {
        setPhase('preparing');
        setStatus(t('Questionnaires.Gaze.Preparing'));
        try {
          controllerRef.current = await recording.prepareGaze();
        } catch (err) {
          console.error('WebGazer preparation failed:', err);
          setPhase('error');
          setBusy(false);
          return;
        }
      }

      const controller = controllerRef.current;
      attemptRef.current += 1;

      // Fresh model each attempt.
      controller.clear();

      setPhase('calibrating');
      controller.setDebugDot(false); // hidden while calibrating (predictions still noisy)
      await controller.calibrate(containerRef.current, ({ lap, laps, step, steps }) => {
        setStatus(t('Questionnaires.Gaze.Calibrating', { lap, laps, step, steps }));
      });

      // Calibration done — show the test instructions and let the respondent
      // start the accuracy check when ready.
      setPhase('testIntro');
      setBusy(false);
    } catch (err) {
      console.error('Calibration round failed:', err);
      setPhase('error');
      setBusy(false);
    }
  }, [recording, t]);

  // Step 2: run the accuracy test against the current calibration and grade it.
  const runTest = useCallback(async () => {
    setBusy(true);
    try {
      const controller = controllerRef.current;
      if (!controller) {
        setPhase('error');
        setBusy(false);
        return;
      }

      // Optional live gaze dot during the accuracy test (opt-in ?gazeDebug=1).
      const debug = typeof window !== 'undefined'
        && new URLSearchParams(window.location.search).get('gazeDebug') === '1';

      setPhase('testing');
      controller.setDebugDot(debug); // show live gaze during the test when debugging
      const result = await controller.test(containerRef.current, ({ point, points }) => {
        setStatus(t('Questionnaires.Gaze.Testing', { point, points }));
      });
      controller.setDebugDot(false);

      const err = result.meanError;
      if (err != null && (bestErrorRef.current == null || err < bestErrorRef.current)) {
        bestErrorRef.current = err;
      }

      if (err != null && err <= ACCEPTABLE_ERROR_PX) {
        setPhase('passed');
        setBusy(false);
        return;
      }

      setLastError(err);
      setPhase(attemptRef.current >= MAX_CALIBRATION_ATTEMPTS ? 'exhausted' : 'fail');
      setBusy(false);
    } catch (err) {
      console.error('Accuracy test failed:', err);
      setPhase('error');
      setBusy(false);
    }
  }, [t]);

  // Positional error is measured in viewport pixels; respondents see it as an
  // "off by X%" of the screen (share of the viewport diagonal) instead, which is
  // intuitive and screen-size independent.
  const toOffPct = (px) => {
    if (px == null) return null;
    const diag = Math.hypot(window.innerWidth || 1, window.innerHeight || 1);
    return Math.max(0, Math.round((px / diag) * 100));
  };
  const roundOffPct = toOffPct(lastError);
  const bestOffPct = toOffPct(bestErrorRef.current);
  const targetOffPct = toOffPct(ACCEPTABLE_ERROR_PX);
  const isActive = phase === 'calibrating' || phase === 'testing' || phase === 'preparing';

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      {/* Full-viewport layer the WebGazer calibration/test dots are drawn into. */}
      <div
        ref={containerRef}
        className="fixed inset-0 pointer-events-none"
        style={{ zIndex: 2147483000 }}
      />

      {/* Status text, 100px above centre so it never covers the centre dot. */}
      {isActive && (
        <div
          className="fixed left-1/2 bg-gray-900/90 text-white text-base font-medium px-5 py-3 rounded-2xl shadow-lg text-center max-w-md"
          style={{ zIndex: 2147483001, top: 'calc(50% - 100px)', transform: 'translate(-50%, -50%)' }}
        >
          {status || t('Questionnaires.Gaze.PleaseWait')}
        </div>
      )}

      {/* Instruction / result card (hidden while the dots are being shown). */}
      {!isActive && (
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-lg w-full text-center">
          {phase === 'intro' && (
            <>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('Questionnaires.Gaze.IntroTitle')}</h1>
              <p className="text-gray-600 text-sm mb-6">{t('Questionnaires.Gaze.IntroDesc')}</p>
              <ul className="text-left text-xs text-gray-500 space-y-1 mb-6">
                <li>• {t('Questionnaires.Gaze.Tip1')}</li>
                <li>• {t('Questionnaires.Gaze.Tip2')}</li>
                <li>• {t('Questionnaires.Gaze.Tip3')}</li>
              </ul>
            </>
          )}

          {phase === 'testIntro' && (
            <>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('Questionnaires.Gaze.TestIntroTitle')}</h1>
              <p className="text-gray-600 text-sm mb-6">{t('Questionnaires.Gaze.TestIntroDesc')}</p>
              <ul className="text-left text-xs text-gray-500 space-y-1 mb-6">
                <li>• {t('Questionnaires.Gaze.TestTip1')}</li>
                <li>• {t('Questionnaires.Gaze.TestTip2')}</li>
              </ul>
            </>
          )}

          {phase === 'fail' && (
            <>
              <h1 className="text-2xl font-bold text-amber-700 mb-2">{t('Questionnaires.Gaze.FailTitle')}</h1>
              <p className="text-gray-600 text-sm mb-6">
                {t('Questionnaires.Gaze.FailDesc', { error: roundOffPct, target: targetOffPct })}
              </p>
            </>
          )}

          {phase === 'exhausted' && (
            <>
              <h1 className="text-2xl font-bold text-amber-700 mb-2">{t('Questionnaires.Gaze.ExhaustedTitle')}</h1>
              <p className="text-gray-600 text-sm mb-6">
                {t('Questionnaires.Gaze.ExhaustedDesc', { error: roundOffPct, target: targetOffPct })}
              </p>
            </>
          )}

          {phase === 'passed' && (
            <>
              <h1 className="text-2xl font-bold text-green-700 mb-2">{t('Questionnaires.Gaze.PassedTitle')}</h1>
              <p className="text-gray-600 text-sm mb-6">
                {t('Questionnaires.Gaze.PassedDesc', { error: bestOffPct })}
              </p>
            </>
          )}

          {phase === 'error' && (
            <>
              <h1 className="text-2xl font-bold text-amber-700 mb-2">{t('Questionnaires.Gaze.ErrorTitle')}</h1>
              <p className="text-gray-600 text-sm mb-6">{t('Questionnaires.Gaze.ErrorDesc')}</p>
            </>
          )}

          <div className="flex flex-col gap-3">
            {(phase === 'intro' || phase === 'fail') && (
              <button
                type="button"
                onClick={runCalibration}
                disabled={busy}
                className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:bg-blue-300 transition-colors"
              >
                {busy && <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
                {phase === 'intro' ? t('Questionnaires.Gaze.StartButton') : t('Questionnaires.Gaze.RetryButton')}
              </button>
            )}

            {phase === 'testIntro' && (
              <button
                type="button"
                onClick={runTest}
                disabled={busy}
                className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:bg-blue-300 transition-colors"
              >
                {busy && <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
                {t('Questionnaires.Gaze.StartTestButton')}
              </button>
            )}

            {phase === 'passed' && (
              <button
                type="button"
                onClick={() => finish('passed')}
                className="w-full px-5 py-3 rounded-xl bg-green-600 text-white text-sm font-semibold hover:bg-green-700 transition-colors"
              >
                {t('Questionnaires.Gaze.ContinueButton')}
              </button>
            )}

            {/* Proceed-without-gaze escape hatch (always available except on success/intro). */}
            {(phase === 'fail' || phase === 'exhausted' || phase === 'error') && (
              <button
                type="button"
                onClick={() => finish(phase === 'error' ? 'skipped' : 'failed')}
                className="w-full px-5 py-3 rounded-xl bg-gray-100 text-gray-700 text-sm font-semibold hover:bg-gray-200 transition-colors"
              >
                {t('Questionnaires.Gaze.SkipButton')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GazeCalibration;
